import React from 'react';
import { useTranslations } from 'next-intl';
import { InfoTooltip } from '@wwe-portal/ui/components/tooltip/info-tooltip';
import { cn } from '@wwe-portal/ui/lib/lib';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons/icomoon';
import { tryFormatThousands } from '@wwe-portal/utils/format';

function FactionMobileStats({
  isDiagram,
  chart,
  totalWins,
  totalLosses,
  isTotalUpgrade = false,
}: Readonly<{
  isDiagram: boolean;
  chart?: any;
  totalWins: number[];
  totalLosses: number[];
  isTotalUpgrade?: boolean;
  darkMode?: boolean;
}>) {
  const t = useTranslations();

  const renderIconColor = (key) => {
    switch (key) {
      case 1:
        return 'deepSkyBlue';
      case 2:
        return 'orange';
      case 3:
        return '#a043f8';
      case 4:
        return '#12eeb1';
      default:
        return undefined;
    }
  };

  const getIconName = () => {
    if (isDiagram) {
      return 'honour-fill';
    }
    return chart?.firstIcon || 'honour-fill';
  };

  const getSecondIconName = () => {
    if (isDiagram) {
      return 'honour-fill';
    }
    return chart?.secondIcon || 'honour-fill';
  };

  const getIconColor = () => {
    if (isDiagram) {
      return undefined;
    }
    return renderIconColor(chart?.id);
  };

  const responsiveClass = isDiagram ? 'sm:hidden' : 'md:hidden';
  const iconSizeClass = isDiagram ? 'size-4' : 'size-[20px]';
  const hrClass = isDiagram
    ? 'hr-profile my-[30px]'
    : 'bg-borderColor8 my-[30px] h-[1px] w-full border-none opacity-10';

  return (
    <div
      className={`faction-details profile-details bg-bgColor2 mb-10 block rounded-[10px] p-5 ${responsiveClass}`}
    >
      <div
        className={cn('flex w-full flex-col pt-[10px]', {
          'faction-chart-header': isTotalUpgrade,
        })}
      >
        <div className="faction-diagram-titles">
          {isDiagram ? (
            <h5 className="m-0 p-0">{t('faction_total_battle_performance')}</h5>
          ) : (
            <div className="m-0 p-0 text-base font-medium">
              {chart ? t(`${chart.title}`) : null}
            </div>
          )}
          <div className="text-chetwodeBlue m-0 p-0 pt-5 text-sm capitalize">
            {isDiagram ? (
              `${t('last')} ${t('12_month')}`
            ) : chart ? (
              <div className="flex">
                {`${t('last')} ${t('12_month')}`}
                {isTotalUpgrade ? (
                  <InfoTooltip
                    titleClassName="faction-chart-total-upgrade-info flex items-center justify-center ml-[15px]"
                    content={t('superstar_chart_tooltip_desc')}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <hr className={hrClass} />
        <div className="flex w-full flex-col justify-end">
          <div className="flex items-center">
            <span
              className={cn(
                'faction-diagram-items-item-icon mr-[15px] rounded-[10px] p-[5px]',
                isDiagram && 'text-blue700'
              )}
              style={!isDiagram ? { color: getIconColor() } : undefined}
            >
              <IcoMoonIcon
                iconName={getIconName()}
                className={iconSizeClass}
                style={!isDiagram ? { color: getIconColor() } : undefined}
              />
            </span>
            <div>
              {isDiagram ? (
                <span className="text-md text-textColor3 m-0 p-0 pt-[5px] font-medium">
                  {tryFormatThousands(
                    totalWins.reduce((acc, cur) => acc + cur, 0)
                  )}
                </span>
              ) : (
                <h5 className="text-textColor3 m-0 p-0 pt-[5px]">
                  {chart ? tryFormatThousands(chart.firstIconTitle) : null}
                </h5>
              )}
              <p className="text-chetwodeBlue m-0 mt-[5px] p-0 text-sm">
                {isDiagram
                  ? `${t('total_wins')} (${t('12_month')})`
                  : t('last_30_days')}
              </p>
            </div>
          </div>
          <div className="mt-5 flex items-center">
            <span
              className={cn(
                'faction-diagram-items-item-icon mr-[15px] rounded-[10px] p-[5px]',
                isDiagram && 'text-purple800'
              )}
              style={!isDiagram ? { color: getIconColor() } : undefined}
            >
              <IcoMoonIcon
                iconName={getSecondIconName()}
                className={iconSizeClass}
                style={!isDiagram ? { color: getIconColor() } : undefined}
              />
            </span>
            <div>
              {isDiagram ? (
                <span className="text-md text-textColor3 m-0 p-0 pt-[5px] font-medium">
                  {tryFormatThousands(
                    totalLosses.reduce(
                      (acc, cur) => Math.abs(acc) + Math.abs(cur),
                      0
                    )
                  )}
                </span>
              ) : (
                <h5 className="text-textColor3 m-0 p-0 pt-[5px]">
                  {chart ? tryFormatThousands(chart.secondIconTitle) : null}
                </h5>
              )}
              <p className="text-chetwodeBlue m-0 mt-[5px] p-0 text-sm">
                {isDiagram
                  ? `${t('total_losses')} (${t('12_month')})`
                  : t('All_time')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FactionMobileStats;
